import i18n from "../i18n";
import { toIntlLocale } from "../lib/date-utils";

// Le namespace `polls` est enregistré au runtime par l'app date-polls ;
// helper non typé pour y accéder depuis le package core.
const t = (key: string, opts?: Record<string, unknown>): string =>
  i18n.t(key as never, opts as never) as unknown as string;
const intl = () => toIntlLocale(i18n.language);

interface ExportData {
  poll: {
    id: string;
    title: string;
    description?: string;
    created_at: string;
    dates: string[];
  };
  votes: Array<{
    id: string;
    voter_name: string;
    voter_email?: string;
    selections: Record<string, string>; // dateId -> voteType
    created_at: string;
  }>;
}

/**
 * Service d'export pour les sondages de dates
 */
export class DatePollExportService {
  /**
   * Exporte les résultats au format CSV
   */
  static exportToCSV(data: ExportData): string {
    const headers = [
      t("polls:export_h_date"),
      t("polls:export_h_participant"),
      t("polls:export_h_email"),
      t("polls:export_h_vote"),
      t("polls:export_h_vote_date"),
    ];
    const rows = [headers.map((h) => this.escapeCsvCell(h)).join(",")];

    // Pour chaque date, lister tous les votes
    data.poll.dates.forEach((date) => {
      data.votes.forEach((vote) => {
        const voteValue = vote.selections[date] || "not_voted";
        const voteText = this.formatVoteValue(voteValue);

        rows.push(
          [
            date,
            vote.voter_name,
            vote.voter_email || "",
            voteText,
            new Date(vote.created_at).toLocaleString(intl()),
          ]
            .map((c) => this.escapeCsvCell(c))
            .join(","),
        );
      });
    });

    // BOM UTF-8 pour qu'Excel sur Windows détecte l'encodage correctement.
    return "\uFEFF" + rows.join("\r\n");
  }

  /**
   * Échappe une cellule CSV selon RFC 4180 + neutralise les attaques
   * "CSV formula injection" (Excel/Sheets/LibreOffice) et le spoofing via
   * caractères de contrôle bidi Unicode.
   */
  private static escapeCsvCell(value: unknown): string {
    let s = value == null ? "" : String(value);
    // Strip chars bidi Unicode (RLO/LRO/PDF/LRI/RLI/FSI/PDI) — anti-spoofing.
    s = s.replace(/[‪-‮⁦-⁩]/g, "");
    // Préfixe les leaders dangereux (formule Excel/Sheets, DDE) avec une apostrophe.
    if (/^[=+\-@\t\r]/.test(s)) {
      s = "'" + s;
    }
    // RFC 4180 : double les quotes internes, puis wrap dans des quotes.
    return `"${s.replace(/"/g, '""')}"`;
  }

  /**
   * Exporte les résultats au format JSON
   */
  static exportToJSON(data: ExportData): string {
    const exportData = {
      poll: {
        id: data.poll.id,
        title: data.poll.title,
        description: data.poll.description,
        created_at: data.poll.created_at,
        dates: data.poll.dates,
      },
      summary: this.generateSummary(data),
      votes: data.votes.map((vote) => ({
        participant: vote.voter_name,
        email: vote.voter_email,
        votes: vote.selections,
        voted_at: vote.created_at,
      })),
      exported_at: new Date().toISOString(),
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Exporte les résultats au format Markdown
   */
  static exportToMarkdown(data: ExportData): string {
    const summary = this.generateSummary(data);

    let markdown = `# ${data.poll.title}\n\n`;

    if (data.poll.description) {
      markdown += `${data.poll.description}\n\n`;
    }

    markdown += `**${t("polls:export_created_on")} :** ${new Date(data.poll.created_at).toLocaleDateString(intl())}\n`;
    markdown += `**${t("polls:export_participants")} :** ${data.votes.length}\n`;
    markdown += `**${t("polls:export_proposed_dates")} :** ${data.poll.dates.length}\n\n`;

    // Résumé des votes par date
    markdown += `## ${t("polls:export_votes_summary")}\n\n`;
    markdown += `| ${t("polls:export_h_date")} | ${t("common:yes")} | ${t("common:maybe")} | ${t("common:no")} | ${t("polls:export_total")} |\n`;
    markdown += `|------|-----|-----------|-----|-------|\n`;

    data.poll.dates.forEach((date) => {
      const dateSummary = summary.byDate[date] || {
        yes: 0,
        maybe: 0,
        no: 0,
        total: 0,
      };
      markdown += `| ${date} | ${dateSummary.yes} | ${dateSummary.maybe} | ${dateSummary.no} | ${dateSummary.total} |\n`;
    });

    markdown += `\n## ${t("polls:export_votes_detail")}\n\n`;

    // Tableau détaillé
    markdown += `| ${t("polls:export_h_participant")} | ${t("polls:export_h_email")} | ${data.poll.dates.map((d) => d).join(" | ")} |\n`;
    markdown += `|------------|-------|${data.poll.dates.map(() => "------").join(" | ")} |\n`;

    data.votes.forEach((vote) => {
      const votes = data.poll.dates.map((date) => {
        const voteValue = vote.selections[date] || "not_voted";
        return this.formatVoteValue(voteValue);
      });

      markdown += `| ${vote.voter_name} | ${vote.voter_email || ""} | ${votes.join(" | ")} |\n`;
    });

    markdown += `\n---\n*${t("polls:export_exported_on", { date: new Date().toLocaleString(intl()) })}*`;

    return markdown;
  }

  /**
   * Exporte les résultats au format PDF (requiert jsPDF)
   */
  static async exportToPDF(data: ExportData): Promise<Blob> {
    // Import dynamique pour éviter les erreurs si jsPDF n'est pas installé
    const { jsPDF } = await import("jspdf");

    const doc = new jsPDF();
    const summary = this.generateSummary(data);

    // Titre
    doc.setFontSize(20);
    doc.text(data.poll.title, 20, 20);

    // Métadonnées
    doc.setFontSize(12);
    let yPosition = 35;
    doc.text(
      `${t("polls:export_created_on")}: ${new Date(data.poll.created_at).toLocaleDateString(intl())}`,
      20,
      yPosition,
    );
    yPosition += 10;
    doc.text(
      `${t("polls:export_participants")}: ${data.votes.length}`,
      20,
      yPosition,
    );
    yPosition += 10;
    doc.text(
      `${t("polls:export_proposed_dates")}: ${data.poll.dates.length}`,
      20,
      yPosition,
    );
    yPosition += 20;

    // Résumé des votes
    doc.setFontSize(14);
    doc.text(`${t("polls:export_votes_summary")}:`, 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    data.poll.dates.forEach((date) => {
      const dateSummary = summary.byDate[date] || {
        yes: 0,
        maybe: 0,
        no: 0,
        total: 0,
      };
      const line = `${date}: ${t("common:yes")}(${dateSummary.yes}) ${t("common:maybe")}(${dateSummary.maybe}) ${t("common:no")}(${dateSummary.no})`;
      doc.text(line, 20, yPosition);
      yPosition += 8;

      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
    });

    // Détail des votes
    yPosition += 15;
    doc.setFontSize(14);
    doc.text(`${t("polls:export_votes_detail")}:`, 20, yPosition);
    yPosition += 10;

    doc.setFontSize(8);
    data.votes.forEach((vote) => {
      const votes = data.poll.dates
        .map((date) => {
          const voteValue = vote.selections[date] || "-";
          return this.formatVoteValue(voteValue).charAt(0);
        })
        .join(" ");

      const line = `${vote.voter_name}: ${votes}`;
      doc.text(line, 20, yPosition);
      yPosition += 6;

      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
    });

    return new Blob([doc.output("blob")], { type: "application/pdf" });
  }

  /**
   * Formate la valeur du vote pour l'affichage
   */
  private static formatVoteValue(value: string): string {
    switch (value) {
      case "yes":
        return t("common:yes");
      case "maybe":
        return t("common:maybe");
      case "no":
        return t("common:no");
      default:
        return t("polls:export_not_voted");
    }
  }

  /**
   * Génère un résumé des votes
   */
  private static generateSummary(data: ExportData) {
    const summary = {
      total: data.votes.length,
      byDate: {} as Record<
        string,
        { yes: number; maybe: number; no: number; total: number }
      >,
      bestDate: null as string | null,
      worstDate: null as string | null,
    };

    data.poll.dates.forEach((date) => {
      summary.byDate[date] = { yes: 0, maybe: 0, no: 0, total: 0 };
    });

    data.votes.forEach((vote) => {
      data.poll.dates.forEach((date) => {
        const voteValue = vote.selections[date];
        if (
          voteValue &&
          (voteValue === "yes" || voteValue === "maybe" || voteValue === "no")
        ) {
          summary.byDate[date][voteValue]++;
          summary.byDate[date].total++;
        }
      });
    });

    // Trouver la meilleure et la pire date
    let bestScore = -1;
    let worstScore = Infinity;

    Object.entries(summary.byDate).forEach(([date, stats]) => {
      const score = stats.yes - stats.no;
      if (score > bestScore) {
        bestScore = score;
        summary.bestDate = date;
      }
      if (score < worstScore) {
        worstScore = score;
        summary.worstDate = date;
      }
    });

    return summary;
  }
}
